/*
 * THE SHARE IMAGE. Guillaume, 2026-09-22: "Every broker, when you close a
 * trade, you got this little button that will display like the logo of the
 * broker and the stats of your trades if you want to flex your profit, we
 * absolutely need that for Trades AND for sessions."
 *
 * Drawn on a canvas in the browser -- no library, no server round trip -- as a
 * 1200x675 PNG (the size X and Telegram preview without cropping).
 */

import i18n from '@/i18n';
import { fmtPct, fmtUsd } from '@/lib/format';

export interface ShareCardData {
  /** "BTCUSDT · LONG 20x" for a trade, the trader's name for a session. */
  title: string;
  subtitle?: string;
  /** The headline number, e.g. ROE for a trade, ROI for a session. */
  headline: number;
  headlineLabel: string;
  /** A share of something (a win rate): no "+", whole number. Default: a signed return. */
  headlinePlain?: boolean;
  /** The dollar result under the headline. */
  pnlUsd: number;
  /** Up to six small label/value pairs. */
  stats: { label: string; value: string }[];
}

const W = 1200;
const H = 675;

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const money = (v: number) =>
  `${v >= 0 ? '+' : ''}${fmtUsd(v, 2)}`;

export async function drawShareCard(data: ShareCardData): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const win = data.pnlUsd >= 0;
  const accent = win ? '#22C55E' : '#EF4444';

  // Background: deep navy with a glow on the side of the result.
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0B1020');
  bg.addColorStop(1, '#111A33');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W * 0.78, H * 0.35, 20, W * 0.78, H * 0.35, 520);
  glow.addColorStop(0, win ? 'rgba(34,197,94,0.28)' : 'rgba(239,68,68,0.25)');
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);
  const blue = ctx.createRadialGradient(80, H, 10, 80, H, 520);
  blue.addColorStop(0, 'rgba(59,130,246,0.25)');
  blue.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = blue;
  ctx.fillRect(0, 0, W, H);

  // Logo, top left, with the Orb beside it -- the lockup from the login page
  // (Guillaume, 2026-09-22: "just add the Orb Next to the CopyFi in the logo").
  const logo = await loadImage('/copyfi_logo.png');
  const orb = await loadImage('/copyfilogo.png');
  if (logo) {
    const h = 64;
    const w = (logo.width / logo.height) * h;
    ctx.drawImage(logo, 64, 52, w, h);
    if (orb) {
      const oh = 76;
      const ow = (orb.width / orb.height) * oh;
      ctx.drawImage(orb, 64 + w + 18, 46, ow, oh);
    }
  } else {
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '700 44px system-ui, sans-serif';
    ctx.fillText('CopyFi', 64, 100);
  }

  // Title and subtitle.
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 46px system-ui, sans-serif';
  ctx.fillText(data.title, 64, 200);
  if (data.subtitle) {
    ctx.fillStyle = '#94A3B8';
    ctx.font = '400 28px system-ui, sans-serif';
    ctx.fillText(data.subtitle, 64, 244);
  }

  // Headline.
  ctx.fillStyle = '#94A3B8';
  ctx.font = '600 26px system-ui, sans-serif';
  ctx.fillText(data.headlineLabel.toUpperCase(), 64, 318);
  ctx.fillStyle = accent;
  ctx.font = '800 128px system-ui, sans-serif';
  ctx.fillText(data.headlinePlain ? fmtPct(data.headline, 0) : `${data.headline >= 0 ? '+' : ''}${fmtPct(data.headline, 2)}`, 56, 440);
  ctx.font = '700 44px system-ui, sans-serif';
  ctx.fillText(money(data.pnlUsd), 64, 500);

  // Stats, right column.
  const x = 760;
  let y = 200;
  for (const s of data.stats.slice(0, 6)) {
    ctx.fillStyle = '#94A3B8';
    ctx.font = '500 22px system-ui, sans-serif';
    ctx.fillText(s.label, x, y);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '700 30px system-ui, sans-serif';
    ctx.fillText(s.value, x, y + 36);
    y += 74;
  }

  // Footer.
  ctx.fillStyle = 'rgba(148,163,184,0.25)';
  ctx.fillRect(64, H - 92, W - 128, 1);
  ctx.fillStyle = '#94A3B8';
  ctx.font = '500 24px system-ui, sans-serif';
  ctx.fillText(i18n.t('messages:shareCard.tagline'), 64, H - 44);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 26px system-ui, sans-serif';
  ctx.fillText('app.copyfi.ai', W - 64, H - 44);
  ctx.textAlign = 'left';

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}
